import { EntityManager } from '../../../engine/ecs/core/EntityManager';
import { ComponentManager } from '../../../engine/ecs/core/ComponentManager';
import { Entity } from '../../../engine/ecs/core/Entity';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import {
  SpriteComponent,
  RenderingType,
} from '../../../engine/ecs/components/SpriteComponent';
import {
  CollisionComponent,
  CollisionShape,
} from '../../../engine/ecs/components/CollisionComponent';
import {
  CollectibleComponent,
  CollectibleType,
} from '../components/CollectibleComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';
import { MazeLevel, CellType } from '../data/MazeData';
import { EnvironmentalAnimationSystem } from '../systems/EnvironmentalAnimationSystem';
import { SystemManager } from '../../../engine/ecs/core/SystemManager';

/**
 * MazeEntityFactory - Creates maze elements
 */
export class MazeEntityFactory {
  private entityManager: EntityManager;
  private componentManager: ComponentManager;
  private systemManager: SystemManager;
  private environmentalSystem?: EnvironmentalAnimationSystem;

  constructor(systemManager: SystemManager) {
    this.entityManager = EntityManager.getInstance();
    this.componentManager = ComponentManager.getInstance();
    this.systemManager = systemManager;
  }

  /**
   * Set the EnvironmentalAnimationSystem for wall melting effects
   */
  public setEnvironmentalSystem(
    environmentalSystem: EnvironmentalAnimationSystem
  ): void {
    this.environmentalSystem = environmentalSystem;
  }

  /**
   * Create the entire maze from maze data
   */
  public createMaze(mazeData: MazeLevel): Entity[] {
    const cellSize = mazeData.cellSize;
    const entities: Entity[] = [];

    for (let y = 0; y < mazeData.height; y++) {
      for (let x = 0; x < mazeData.width; x++) {
        const cellType = mazeData.maze[y][x];
        const pixelX = x * cellSize + cellSize / 2;
        const pixelY = y * cellSize + cellSize / 2;

        switch (cellType) {
          case CellType.Wall:
            entities.push(this.createWall(pixelX, pixelY, cellSize));
            break;
          case CellType.Dot:
            entities.push(this.createDot(pixelX, pixelY));
            break;
          case CellType.PowerPellet:
            entities.push(this.createPowerPellet(pixelX, pixelY));
            break;
          // Pacman and Ghost spawn points are handled by their respective factories
        }
      }
    }

    return entities;
  }

  /**
   * Create a wall entity with optional desert decorations
   */
  private createWall(x: number, y: number, size: number): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - ocean wall with light sky blue color
    const sprite = new SpriteComponent(
      '#a2d9ef', //#a2d9ef Light sky blue for ocean wall    #87CEEB
      new Vector2(size, size), // Full cell size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      0, // z-index (background)
      RenderingType.AtlanticWall // Use Atlantic wall rendering type
    );
    this.componentManager.addComponent(entity, sprite);

    // Add a water cap as a separate entity (light blue rectangle on top of wall)
    const waterCapEntity = this.entityManager.createEntity();
    const waterCapTransform = new TransformComponent(
      new Vector2(x, y - size / 2 + 4), // Slightly above the wall
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(waterCapEntity, waterCapTransform);
    const waterCapSprite = new SpriteComponent(
      '#B0E0E6', // Powder blue for water cap
      new Vector2(size, size * 0.25), // Thin water cap
      undefined,
      true,
      0.95,
      1, // z-index above wall
      RenderingType.Rectangle
    );
    this.componentManager.addComponent(waterCapEntity, waterCapSprite);

    // Collision component - solid wall
    const collision = new CollisionComponent(
      CollisionShape.Rectangle, // rectangular collision
      new Vector2(size, size), // collision size
      new Vector2(0, 0), // offset
      false, // not a trigger
      'wall', // collision layer
      [] // walls don't actively collide
    );
    this.componentManager.addComponent(entity, collision);

    // Initialize environmental animation effects for this wall
    // This demonstrates proper ECS animation integration
    this.initializeWallEnvironmentalEffects(entity.getId());

    // Randomly add ocean decorations to some walls (2% chance - much cleaner)
    if (Math.random() < 0.02) {
      this.createOceanDecoration(x, y, size);
    }

    return entity;
  }

  /**
   * Initialize environmental animation effects for a wall entity
   * Demonstrates proper ECS animation system integration
   */
  private initializeWallEnvironmentalEffects(entityId: number): void {
    // Use the stored environmental animation system to initialize wall erosion
    if (this.environmentalSystem) {
      this.environmentalSystem.initializeWallErosion(entityId);
    }
  }

  /**
   * Create a desert decoration on a wall
   */
  private createOceanDecoration(x: number, y: number, size: number): void {
    const decorationEntity = this.entityManager.createEntity();

    // Transform component - position the decoration on the wall
    const decorationTransform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(decorationEntity, decorationTransform);

    // Choose a random ocean decoration
    const decorations = [
      { symbol: '🐋', color: '#1E90FF', name: 'whale' }, // Whale
      { symbol: '🐠', color: '#1E90FF', name: 'fish' }, // Fish
      { symbol: '🦈', color: '#4169E1', name: 'shark' }, // Shark
      { symbol: '🐙', color: '#9370DB', name: 'octopus' }, // Octopus
      // { symbol: '🦑', color: '#8A2BE2', name: 'squid' }, // Squid
      // { symbol: '🦐', color: '#FF6347', name: 'shrimp' }, // Shrimp
      // { symbol: '🦞', color: '#DC143C', name: 'lobster' }, // Lobster
      // { symbol: '🦀', color: '#FF4500', name: 'crab' }, // Crab
      // { symbol: '🐡', color: '#FF6B6B', name: 'pufferfish' }, // Pufferfish
      { symbol: '🐢', color: '#228B22', name: 'turtle' }, // Sea turtle
      { symbol: '🦭', color: '#696969', name: 'seal' }, // Seal
      // { symbol: '🦦', color: '#8B4513', name: 'otter' }, // Sea otter
      { symbol: '🦈', color: '#191970', name: 'deep_shark' }, // Deep sea shark
      // { symbol: '🐚', color: '#F5DEB3', name: 'shell' }, // Shell
      // { symbol: '🪸', color: '#FF69B4', name: 'coral' }, // Coral
      { symbol: '🌊', color: '#87CEEB', name: 'wave' }, // Wave
    ];

    const decoration =
      decorations[Math.floor(Math.random() * decorations.length)];

    // Sprite component - ocean decoration
    const decorationSprite = new SpriteComponent(
      decoration.color,
      new Vector2(size * 0.6, size * 0.6), // Smaller than wall
      decoration.symbol, // Use the emoji as texture
      true, // visible
      0.9, // slightly transparent
      2, // z-index above wall and ocean cap
      RenderingType.DesertDecoration // New rendering type for decorations
    );
    this.componentManager.addComponent(decorationEntity, decorationSprite);

    // Register with systems
    this.systemManager.addEntity(decorationEntity);
  }

  /**
   * Create a dot entity
   */
  private createDot(x: number, y: number): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - small ocean blue dot (ocean pearl)
    const sprite = new SpriteComponent(
      '#1E90FF', // Deep blue color - same as power pellets
      new Vector2(5, 5), // Small dot size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      1, // z-index
      RenderingType.Circle // Use generic circle rendering for dots
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component - trigger for collection
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(6, 6), // collision size
      new Vector2(0, 0), // offset
      true, // is a trigger
      'collectible', // collision layer
      ['pacman'] // only pacman can collect
    );
    this.componentManager.addComponent(entity, collision);

    // Collectible component
    const collectible = new CollectibleComponent(
      CollectibleType.Dot, // dot type
      10 // 10 points
    );
    this.componentManager.addComponent(entity, collectible);

    return entity;
  }

  /**
   * Create a power pellet entity
   */
  private createPowerPellet(x: number, y: number): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - large ocean blue power pellet (glowing ocean orb)
    const sprite = new SpriteComponent(
      '#1E90FF', // Deep blue color for ocean theme
      new Vector2(14, 14), // Larger size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      1, // z-index
      RenderingType.PowerPellet // Use generic power pellet rendering
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component - trigger for collection
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(14, 14), // collision size
      new Vector2(0, 0), // offset
      true, // is a trigger
      'collectible', // collision layer
      ['pacman'] // only pacman can collect
    );
    this.componentManager.addComponent(entity, collision);

    // Collectible component - power pellet
    const collectible = new CollectibleComponent(
      CollectibleType.PowerPellet, // power pellet type
      50 // 50 points
    );
    this.componentManager.addComponent(entity, collectible);

    return entity;
  }

  /**
   * Create a bonus fruit entity
   */
  public createBonusFruit(
    x: number,
    y: number,
    fruitType: CollectibleType,
    points: number
  ): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - fruit color (varies by type)
    let color = '#FF0000'; // Default red
    switch (fruitType) {
      case CollectibleType.Cherry:
        color = '#FF0000';
        break;
      case CollectibleType.Strawberry:
        color = '#FF69B4';
        break;
      case CollectibleType.Orange:
        color = '#FFA500';
        break;
      case CollectibleType.Apple:
        color = '#FF0000';
        break;
      default:
        color = '#FF0000';
    }

    const sprite = new SpriteComponent(
      color, // Fruit color
      new Vector2(16, 16), // Medium size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      1 // z-index
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(16, 16), // collision size
      new Vector2(0, 0), // offset
      true, // is a trigger
      'collectible', // collision layer
      ['pacman'] // only pacman can collect
    );
    this.componentManager.addComponent(entity, collision);

    // Collectible component - bonus fruit
    const collectible = new CollectibleComponent(fruitType, points);
    this.componentManager.addComponent(entity, collectible);

    return entity;
  }
}
